import { useState } from 'react';
import { Download, Lock } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  DataTable,
  PageShell,
  Pagination,
  SectionCard,
  Skeleton,
  StatusBadge,
  toast,
  type DataTableColumn,
} from '@/components/ui';
import { formatBytes, OrderExportError } from './lib';
import { AdminNameCell } from '@/features/admin/components/AdminNameCell';
import {
  downloadOrderExportFile,
  useOrderExportJobsQuery,
} from './useOrderExportJobs';
import type { OrderExportJob } from './types';

const PAGE_SIZE = 20;

const KST = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});
const kst = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso.replace(/(\.\d{3})\d+/, '$1'));
  if (Number.isNaN(d.getTime())) return null;
  const p = KST.formatToParts(d);
  const v = (t: Intl.DateTimeFormatPartTypes) => p.find((x) => x.type === t)?.value ?? '';
  return `${v('year')}-${v('month')}-${v('day')} ${v('hour')}:${v('minute')}`;
};

const STATUS_VARIANT: Record<string, 'pending' | 'progress' | 'success' | 'danger' | 'neutral'> = {
  PENDING: 'pending',
  RUNNING: 'progress',
  COMPLETED: 'success',
  FAILED: 'danger',
  EXPIRED: 'neutral',
};
const STATUS_LABEL: Record<string, string> = {
  PENDING: '대기 중',
  RUNNING: '생성 중',
  COMPLETED: '완료',
  FAILED: '실패',
  EXPIRED: '보관 만료',
};

const errorText = (err: unknown, fallback: string): string =>
  err instanceof OrderExportError && err.showBeMessage ? err.message : fallback;

/**
 * 주문 엑셀 다운로드 내역 — 내 요청, 진행률 자동 갱신, 파일별 다운로드. 다운로드 이력은 로그라 삭제하지 않는다(QA-332,
 * 보관 기한이 지나면 파일만 만료). 요청자 이름 표시. 헤더는 모두 가운데, 칸 너비는 내용에 맞춤.
 */
export const OrderExportJobsPage = () => {
  const { t } = useTranslation('orders');
  const [page, setPage] = useState(1);
  const query = useOrderExportJobsQuery(page, PAGE_SIZE);
  const [downloading, setDownloading] = useState<string | null>(null);

  const download = async (job: OrderExportJob, fileNo: number, fileName: string) => {
    const key = `${job.exportJobId}:${fileNo}`;
    setDownloading(key);
    try {
      await downloadOrderExportFile(job.exportJobId, fileNo, fileName);
    } catch (err) {
      toast.error(errorText(err, t('orders.exportJobs.downloadError', '파일을 받지 못했습니다.')));
    } finally {
      setDownloading(null);
    }
  };

  const columns: DataTableColumn<OrderExportJob>[] = [
    {
      key: 'requestedAt',
      header: t('orders.exportJobs.col.requestedAt', '요청일시'),
      width: 160,
      align: 'center',
      cell: (j) => <span className="whitespace-nowrap tabular-nums">{kst(j.requestedAt) ?? '—'}</span>,
    },
    {
      key: 'requestedBy',
      header: t('orders.exportJobs.col.requestedBy', '요청자'),
      width: 120,
      align: 'center',
      cell: (j) => <AdminNameCell adminUserId={j.requestedBy} />,
    },
    {
      key: 'period',
      header: t('orders.exportJobs.col.period', '기간'),
      width: 230,
      align: 'center',
      cell: (j) => (
        <span className="whitespace-nowrap tabular-nums">
          {j.dateFrom ?? ''} ~ {j.dateTo ?? t('orders.exportJobs.untilRequest', '요청일')}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('orders.exportJobs.col.status', '상태'),
      width: 110,
      align: 'center',
      cell: (j) => (
        <div className="flex flex-col items-center gap-1 whitespace-nowrap">
          <StatusBadge
            variant={STATUS_VARIANT[j.status] ?? 'neutral'}
            label={
              j.status === 'RUNNING'
                ? `${t('orders.exportJobs.status.RUNNING', '생성 중')} ${j.progressPercent}%`
                : t(`orders.exportJobs.status.${j.status}`, STATUS_LABEL[j.status] ?? j.status)
            }
          />
          {j.status === 'FAILED' && j.errorMessage ? (
            <span className="text-[12px]" style={{ color: 'var(--status-danger-fg)' }}>
              {j.errorMessage}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'rows',
      header: t('orders.exportJobs.col.rows', '행 수'),
      width: 110,
      align: 'right',
      headerAlign: 'center',
      cell: (j) => (
        <span className="tabular-nums">
          {j.status === 'COMPLETED'
            ? new Intl.NumberFormat('ko-KR').format(j.totalRows)
            : j.totalOrders != null
              ? t('orders.exportJobs.ordersProgress', {
                  done: new Intl.NumberFormat('ko-KR').format(j.processedOrders),
                  total: new Intl.NumberFormat('ko-KR').format(j.totalOrders),
                  defaultValue: '주문 {{done}} / {{total}}',
                })
              : '—'}
        </span>
      ),
    },
    {
      key: 'files',
      header: t('orders.exportJobs.col.files', '파일'),
      width: 240,
      align: 'center',
      cell: (j) =>
        j.status === 'COMPLETED' && j.files.length > 0 ? (
          <div className="flex flex-col items-center gap-1" data-testid="export-job-files">
            {j.passwordProtected ? (
              // 비밀번호를 건 파일 — 요청할 때 정한 비밀번호로 연다(서버에 남지 않음).
              <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: 'var(--content-muted)' }} data-testid="export-job-locked">
                <Lock size={12} aria-hidden="true" />
                {t('orders.exportJobs.passwordProtected', '비밀번호 걸림')}
              </span>
            ) : null}
            {j.files.map((f) => (
              <Button
                key={f.fileNo}
                type="button"
                size="sm"
                variant="outline"
                className="justify-start whitespace-nowrap"
                disabled={downloading === `${j.exportJobId}:${f.fileNo}`}
                onClick={() => void download(j, f.fileNo, f.fileName)}
                title={f.fileName}
              >
                <Download size={14} aria-hidden="true" />
                {t('orders.exportJobs.fileLabel', {
                  no: f.fileNo,
                  rows: new Intl.NumberFormat('ko-KR').format(f.rows),
                  size: formatBytes(f.sizeBytes),
                  defaultValue: '파일 {{no}} · {{rows}}행 · {{size}}',
                })}
              </Button>
            ))}
          </div>
        ) : (
          '—'
        ),
    },
    {
      key: 'expiresAt',
      header: t('orders.exportJobs.col.expiresAt', '보관 기한'),
      width: 160,
      align: 'center',
      cell: (j) => <span className="whitespace-nowrap tabular-nums">{kst(j.expiresAt) ?? '—'}</span>,
    },
  ];

  const total = query.data?.meta?.pagination?.totalItems ?? query.data?.items.length ?? 0;

  return (
    <PageShell
      title={t('orders.exportJobs.title', '엑셀 다운로드 내역')}
      description={t(
        'orders.exportJobs.description',
        '주문 목록에서 요청한 엑셀 파일을 받습니다. 생성 중인 요청은 자동으로 새로고침되며, 완료된 파일은 7일 동안 보관됩니다.',
      )}
      backHref="/orders"
      backLabel={t('orders.list.title', '주문 관리')}
    >
      <SectionCard padded={false}>
        {query.isPending ? (
          <Skeleton className="m-4 h-24" />
        ) : query.isError ? (
          <p role="alert" className="p-4 text-[13px]" style={{ color: 'var(--status-danger-fg)' }}>
            {errorText(query.error, t('orders.exportJobs.loadError', '다운로드 내역을 불러오지 못했습니다.'))}
          </p>
        ) : (
          <DataTable
            columns={columns}
            rows={query.data.items}
            rowKey={(j) => j.exportJobId}
            ariaLabel={t('orders.exportJobs.title', '엑셀 다운로드 내역')}
            emptyState={t('orders.exportJobs.empty', '요청한 엑셀이 없습니다. 주문 목록의 엑셀 다운로드에서 요청하세요.')}
          />
        )}
      </SectionCard>
      {total > PAGE_SIZE ? (
        <Pagination
          total={total}
          currentPage={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      ) : null}
    </PageShell>
  );
};
